import { ReactNode, useEffect, useState } from 'react';
import logger from '@logger';
import { useTranslations } from 'next-intl';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import useCustomer from '@hooks/useCustomer';
import trackCompletedSection from '@helpers/analyticsHelpers/trackCompletedSection';
import trackPlaceOrderClick from '@helpers/analyticsHelpers/trackPlaceOrderClick';
import { PossiblyWrongStoreConfirmedState } from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton';
import {
  selectCheckoutPayment,
  setCompleted,
  setEditing,
  setErrorMessage,
  setSelectedMode,
} from '@slices/checkoutSlice';
import {
  StyledCheckoutSubmitButton,
  StyledSwedbankPayLogo,
  StyledText,
} from '@molecules/CheckoutPaymentButton/CheckoutPaymentButton.styles';
import { CheckoutError, placeOrder } from '@api/interfaces/checkoutApi';
import { CreditCardPaymentInfoData } from '@occ/api-client';
import useCheckCartConflict from '@hooks/useCheckCartConflict';
import { PlaceOrderErrorResponse } from '@enums/PlaceOrderErrorResponse';
import paths from '@constants/paths';
import { useRouter } from 'next/navigation';
import { setEmptyCartModalVisibility } from '@slices/modalSlice';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useAbortController from '@hooks/useAbortController';
import { setWindowHref } from '@helpers/setWindowHref';

interface Props {
  disabled?: boolean;
  children: ReactNode;
  saveCard?: boolean;
  selectedCard?: CreditCardPaymentInfoData;
  setError: (error: ErrorDataType) => void;
  possiblyWrongStoreConfirmed: PossiblyWrongStoreConfirmedState;
}

const PayExButton = ({
  saveCard = false,
  selectedCard = {
    cardType: '',
    defaultCard: false,
    expireDate: '',
    expired: false,
    id: '',
    maskedNumber: '',
  },
  children,
  disabled = false,
  setError,
  possiblyWrongStoreConfirmed,
}: Props) => {
  const t = useTranslations('checkoutpage');
  const payment = useAppSelector(selectCheckoutPayment);
  const { customer } = useCustomer();
  const { cart } = useCart();
  const checkCartConflict = useCheckCartConflict({ checkStock: true });
  const [disablePayment, setDisablePayment] = useState(disabled);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { store } = useStore();
  const { getSignal } = useAbortController();

  useEffect(() => {
    const activeCards = customer?.savedCards?.filter((card) => !card.expired);
    if (customer?.savedCards?.length && activeCards && activeCards.length > 0) {
      if (payment.selectedCard.id) {
        setDisablePayment(false);
      } else {
        setDisablePayment(true);
      }
    } else {
      setDisablePayment(disabled);
    }
  }, [payment.selectedCard?.id, customer?.savedCards, disabled]);

  const submitPayment = async () => {
    trackPlaceOrderClick();
    setDisablePayment(true);
    setIsSubmitting(true);
    try {
      await checkCartConflict({ storeId: store?.storeId });
      const location = await placeOrder(
        {
          orderRef: cart?.orderReference ?? 'null',
          saveCard: customer?.isAnonymous ? false : saveCard,
          selectedCardId: selectedCard?.id === 'new' || selectedCard?.expired ? '' : (selectedCard?.id as string),
          klarnaAuthorizationToken: '',
          userClickedContinue:
            possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_DELIVERY ||
            possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL,
        },
        getSignal()
      );
      trackCompletedSection('payment');
      // @ts-ignore
      setWindowHref(location);
    } catch (e) {
      if (e instanceof CheckoutError) {
        if (e.details === PlaceOrderErrorResponse.NO_DELIVERY_SLOT) {
          dispatch(setCompleted({ boxName: 'delivery', isCompleted: false }));
          dispatch(setEditing({ boxName: 'delivery', isEditing: true }));
          dispatch(setErrorMessage(e.details));
          setError({ errorCode: e.details });
        } else if (e.details === PlaceOrderErrorResponse.EMPTY_CART) {
          dispatch(setEmptyCartModalVisibility(true));
          router.push(paths.HOME);
        } else if (e.details === PlaceOrderErrorResponse.WRONG_STORE) {
          setError({ errorCode: e.details });
        }
      } else {
        setError({ errorCode: 'default' });
        dispatch(setSelectedMode(''));
        logger.error({ error: 'PayEx error' });
      }
      setDisablePayment(false);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (possiblyWrongStoreConfirmed === PossiblyWrongStoreConfirmedState.CONFIRMED_IN_MODAL) {
      submitPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [possiblyWrongStoreConfirmed]);

  return (
    <>
      <StyledCheckoutSubmitButton
        variant="primary"
        onClick={async (e) => {
          if (!(e.target as HTMLButtonElement)?.closest<HTMLFormElement>('form:invalid')) {
            await submitPayment();
          }
        }}
        type="submit"
        data-testid="payex-checkout-button"
        disabled={disablePayment}
        isSubmitting={isSubmitting}
      >
        {children}
      </StyledCheckoutSubmitButton>
      <StyledText type="body">{t('text.handledBySwedbankPay')}</StyledText>
      <StyledSwedbankPayLogo src="/icons/logos/SwedbankPay.svg" alt="Swedbank Pay Logo" />
    </>
  );
};

export default PayExButton;
